import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkOperationsPanel = ({ 
  selectedCount, 
  onBulkDelete, 
  onBulkExport, 
  onBulkImport,
  onClearSelection 
}) => {
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleImport = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setIsImporting(true);
      setImportProgress(0);
      
      const interval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              setIsImporting(false);
              setImportProgress(0);
              onBulkImport(file);
            }, 500);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
  };

  if (selectedCount === 0 && !isImporting) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="Layers" size={24} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Bulk Operations</h3>
              <p className="text-sm text-muted-foreground">
                Import definitions or export glossary data
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              iconName="Upload"
              iconPosition="left"
              onClick={() => fileInputRef?.current?.click()}
            >
              Import CSV
            </Button>
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={onBulkExport}
            >
              Export All
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isImporting) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Upload" size={24} className="text-accent" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Importing Definitions...
            </h3>
            <p className="text-sm text-muted-foreground">
              Processing CSV file and updating glossary entries
            </p>
          </div>
        </div>
        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
          <div
            className="h-full bg-accent transition-all duration-300 ease-out"
            style={{ width: `${importProgress}%` }}
          />
        </div>
        <p className="text-sm text-muted-foreground text-center mt-2">
          {importProgress}% complete
        </p>
      </div>
    );
  }

  return (
    <div className="bg-accent/5 border border-accent/20 rounded-lg p-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center">
            <Icon name="CheckSquare" size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {selectedCount} {selectedCount === 1 ? 'word' : 'words'} selected
            </h3>
            <p className="text-sm text-muted-foreground">
              Choose a bulk action to apply
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={onBulkExport}
          >
            Export Selected
          </Button>
          <Button
            variant="destructive"
            iconName="Trash2"
            iconPosition="left"
            onClick={onBulkDelete}
          >
            Delete Selected
          </Button>
          <Button
            variant="ghost"
            iconName="X"
            iconPosition="left"
            onClick={onClearSelection}
          >
            Clear Selection
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkOperationsPanel;